import { useEffect, useRef, useState, useCallback } from 'react';
import type * as React from 'react';
import { createPortal } from 'react-dom';
import {
  Input,
  Textarea,
  Button,
  Spinner,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Search24Regular,
  Location24Regular,
  Map24Regular,
  ArrowExpand20Regular,
} from '@fluentui/react-icons';
import { getMapConnector, getGeocodeConnector } from '../../../shared/maps';
import type { MapHandle, AutocompleteSuggestion } from '../../../shared/maps';
import { useIsMobile } from '../hooks/useIsMobile';

interface LocationPickerProps {
  onLocationSelect: (location: { lat: number; lng: number; address: string; name: string }) => void;
  initialLocation?: { lat: number; lng: number; address: string; name?: string } | null;
  /** Map height. Default 400px. */
  height?: string;
  /** Search input size. Default 'medium'. */
  size?: 'small' | 'medium' | 'large';
  /** Start the map on satellite/aerial imagery instead of the streets base.
   *  Aerial is the more useful base for placing a precise service-location
   *  pin. Mobile full-screen already defaults to satellite regardless. */
  defaultSatellite?: boolean;
  /** When true, the map renders inside a modal opened by an "Adjust on map" button.
   *  Useful when the picker is mostly used via address autocomplete and the map
   *  is dead screen real estate the rest of the time. */
  mapInDialog?: boolean;
  /** Controlled dialog open state. When provided, the picker hides its own
   *  trigger button and lets the parent open/close the dialog programmatically.
   *  Only meaningful with `mapInDialog`. */
  dialogOpen?: boolean;
  onDialogOpenChange?: (open: boolean) => void;
  /** Custom footer actions for the dialog. When provided, replaces the
   *  default "Done" button — the parent is responsible for including a way
   *  to close the dialog (typically by calling `onDialogOpenChange(false)`).
   *  Only meaningful with `mapInDialog`. */
  dialogActions?: React.ReactNode;
  /** When provided, renders a Save button inside the pin popover and calls
   *  this with the current pin's lat/lng/address/name when clicked. Lets
   *  the parent commit the pick without rendering its own form chrome. */
  onSave?: (location: { lat: number; lng: number; address: string; name: string }) => void;
  /** Label for the in-popover Save button. Default "Save". */
  saveLabel?: string;
  /** Disables the in-popover Save button (e.g. while a mutation is in flight). */
  saveDisabled?: boolean;
  /** When this value changes, the picker wipes its internal pin state (marker,
   *  selectedLocation, pinName) without unmounting the map. Used by parents
   *  that want "fresh slate after save" without losing map zoom/position. */
  resetSignal?: number | string;
  /** Opt into a flex-fill layout: the picker root and the map both grow to
   *  fill a flex parent's available height. Callers that wire this on should
   *  also pass `height='100%'`. Default (false) preserves the historical
   *  content-sized behavior used by the order form's dialog variant. */
  fillHeight?: boolean;
  /** Additional read-only pins to render on the map for context — e.g. the
   *  customer's previously-saved locations. Rendered as non-draggable neutral
   *  markers with a name tooltip; the editable pin (driven by initialLocation
   *  / left-click / drag) stays separate. Filter out any row currently being
   *  edited so the saved pin doesn't double up with the editable one. */
  savedLocations?: Array<{ id: string; lat: number; lng: number; name?: string }>;
  /** Click handler for the saved-location pins. When provided, clicking a
   *  saved marker calls back to the parent (typically to select the matching
   *  row, which then drives `initialLocation` and shows the editor popover).
   *  When omitted, clicking just pins the hover tooltip + zooms in. */
  onSavedLocationClick?: (id: string) => void;
  /** Where the selected-pin detail editor (name / address / coords / Save)
   *  renders. Default `'popover'` floats it over the map anchored to the pin.
   *  `'panel'` renders it flat into `detailsPortalTarget` — a Google-Maps-style
   *  sidebar the parent owns — and is shown whenever a pin is selected,
   *  regardless of whether the marker is in view. */
  detailsPlacement?: 'popover' | 'panel';
  /** Sidebar container the detail editor portals into when
   *  `detailsPlacement === 'panel'`. Ignored in popover mode. Null until the
   *  parent's host element mounts — the editor simply doesn't render until then. */
  detailsPortalTarget?: HTMLElement | null;
}

// Detect a "lat, lng" or "lat lng" paste so users can drop a pin from raw
// coordinates copied out of Google Maps, a GPS readout, etc.
const parseLatLng = (input: string): { lat: number; lng: number } | null => {
  const match = input
    .trim()
    .match(/^(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (!match) return null;
  const lat = parseFloat(match[1]);
  const lng = parseFloat(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90) return null;
  if (lng < -180 || lng > 180) return null;
  return { lat, lng };
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
    width: '100%',
  },
  rootFill: {
    flex: 1,
    minHeight: 0,
  },
  // Unified search pill — input + button live in one rounded, shadowed surface
  // (mirrors the website picker's [input][search] toolbar) instead of two
  // gapped controls. The pill owns the chrome; the Input sits borderless inside.
  searchRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'stretch',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    boxShadow: tokens.shadow8,
    ':focus-within': {
      ...shorthands.borderColor(tokens.colorBrandStroke1),
    },
  },
  searchInput: {
    flexGrow: 1,
    backgroundColor: 'transparent',
    // The pill draws the border + focus ring — strip the Input's own chrome so
    // the two don't double up.
    ...shorthands.border('none'),
    '::after': {
      display: 'none',
    },
  },
  searchButton: {
    // Square the left corners so the primary button sits flush against the
    // input; only the pill's outer-right corners stay rounded.
    ...shorthands.borderRadius('0', '4px', '4px', '0'),
  },
  suggestList: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 10,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
    maxHeight: '288px',
    overflowY: 'auto',
    listStyleType: 'none',
    ...shorthands.margin('0'),
    ...shorthands.padding('0'),
  },
  suggestItem: {
    width: '100%',
    textAlign: 'left',
    ...shorthands.padding('10px', '12px'),
    ...shorthands.border('none'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'flex-start',
    ...shorthands.gap('10px'),
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    ':last-child': {
      borderBottomWidth: 0,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'default',
    },
  },
  suggestItemHighlighted: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  suggestIcon: {
    flexShrink: 0,
    marginTop: '2px',
    color: tokens.colorNeutralForeground3,
  },
  suggestIconHighlighted: {
    color: tokens.colorBrandForeground1,
  },
  suggestName: {
    fontSize: '13px',
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  suggestPlace: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mapWrap: {
    position: 'relative',
  },
  mapWrapFill: {
    flex: 1,
    minHeight: 0,
  },
  mapContainer: {
    width: '100%',
    ...shorthands.borderRadius('4px'),
    ...shorthands.overflow('hidden'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px'),
  },
  pinPopover: {
    position: 'absolute',
    zIndex: 5,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('6px'),
    ...shorthands.padding('10px', '12px'),
    boxShadow: tokens.shadow16,
    width: '280px',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
  },
  // Panel variant (detailsPlacement="panel"): the same pin-detail editor as
  // the popover, but rendered flat into a parent-supplied sidebar container
  // (via a portal) instead of floating over the map. No arrow, no shadow —
  // it sits inline above the saved-location list. Border/radius match the
  // list rows so it reads as part of the same rail.
  pinPanel: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding('10px', '12px'),
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  pinPopoverArrow: {
    position: 'absolute',
    width: '12px',
    height: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  pinPopoverArrowAbove: {
    bottom: '-7px',
    transform: 'translateX(-50%) rotate(45deg)',
  },
  pinPopoverArrowBelow: {
    top: '-7px',
    transform: 'translateX(-50%) rotate(45deg)',
  },
  pinTitleRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('6px'),
  },
  pinTitleIcon: {
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
    fontSize: '20px',
  },
  // The Fluent Input root draws the field's border + focus underline (::after).
  // The pin name field should read as a borderless inline title, so strip both
  // — pinTitleInput (below) only styles the inner <input> slot, not this root.
  pinTitleInputRoot: {
    flexGrow: 1,
    minWidth: 0,
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    '::after': {
      display: 'none',
    },
  },
  pinTitleInput: {
    flexGrow: 1,
    minWidth: 0,
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    ...shorthands.padding('2px', '6px'),
    ...shorthands.borderRadius('3px'),
    outlineStyle: 'none',
    outlineWidth: '0',
    outlineColor: 'transparent',
    boxShadow: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
    cursor: 'text',
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFaster,
    '::placeholder': {
      color: tokens.colorNeutralForeground3,
      fontWeight: tokens.fontWeightSemibold,
      opacity: 1,
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus': {
      outlineStyle: 'none',
      outlineWidth: '0',
      outlineColor: 'transparent',
      boxShadow: 'none',
      backgroundColor: tokens.colorNeutralBackground1Selected,
    },
    ':focus-visible': {
      outlineStyle: 'none',
      outlineWidth: '0',
      outlineColor: 'transparent',
      boxShadow: 'none',
      backgroundColor: tokens.colorNeutralBackground1Selected,
    },
  },
  selectedAddress: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    userSelect: 'none',
    cursor: 'default',
  },
  // Editable street-address field. Reads as inline body text (like the old
  // read-only line) but is a real Textarea so a long address wraps and can be
  // corrected in place. Border stays subtle until hover/focus.
  addressInputRoot: {
    width: '100%',
    minWidth: 0,
    backgroundColor: 'transparent',
    ...shorthands.border('1px', 'solid', 'transparent'),
    ...shorthands.borderRadius('3px'),
    '::after': {
      display: 'none',
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-within': {
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
      backgroundColor: tokens.colorNeutralBackground1,
    },
  },
  addressInput: {
    fontFamily: 'inherit',
    fontSize: '13px',
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground1,
    ...shorthands.padding('2px', '4px'),
    minHeight: '20px',
    resize: 'none',
  },
  selectedCoords: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontFamily: 'monospace',
    alignSelf: 'flex-start',
    userSelect: 'none',
    cursor: 'default',
  },
  styleToggle: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    ...shorthands.borderRadius('4px'),
  },
  mapControls: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    zIndex: 6,
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
    alignItems: 'flex-end',
  },
  searchOverlay: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    right: '120px',
    maxWidth: '480px',
    zIndex: 6,
  },
  // Full-width search band for the mobile full-screen picker. Honors the
  // top safe-area inset so it clears the notch / status bar.
  searchOverlayMobile: {
    position: 'absolute',
    top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
    left: '12px',
    right: '12px',
    zIndex: 6,
  },
  // Map controls (satellite toggle) pushed below the full-width search band
  // so they don't collide with it on mobile.
  mapControlsMobile: {
    position: 'absolute',
    top: 'calc(env(safe-area-inset-top, 0px) + 64px)',
    right: '12px',
    zIndex: 6,
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
    alignItems: 'flex-end',
  },
  // Full-screen surface for the mobile picker — strip the dialog's chrome
  // (padding, max-width, radius, margin) so the map fills the viewport.
  // Invisible focus sink. Fluent's Dialog auto-focuses the first focusable
  // element on open (useFocusFirstElement); without this the search Input
  // grabs focus and the mobile keyboard springs up. A tabIndex=-1 element
  // placed first absorbs that initial focus silently — the user must tap the
  // search field to focus it intentionally. Kept rendered (not display:none)
  // so Tabster still treats it as focusable.
  focusSink: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    opacity: 0,
    pointerEvents: 'none',
    ...shorthands.overflow('hidden'),
  },
  mobileSurface: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100dvh',
    maxWidth: '100vw',
    maxHeight: '100dvh',
    ...shorthands.margin('0'),
    ...shorthands.padding('0'),
    ...shorthands.borderRadius('0'),
    ...shorthands.border('none'),
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.overflow('hidden'),
  },
  // Cancel / Add buttons overlaid on the bottom of the map. Solid token
  // background (not a scrim) keeps the buttons legible over satellite tiles.
  mobileActionBar: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 6,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    ...shorthands.gap('8px'),
    ...shorthands.padding('12px'),
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    boxShadow: tokens.shadow8,
  },
  zoomExtentsButton: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    ...shorthands.borderRadius('4px'),
  },
  dialogHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: '-4px',
    marginBottom: '4px',
  },
  // Desktop dialog layout: a left rail (hint + selected-pin detail panel) beside
  // the map, mirroring the customer-locations tab. Keeps the pin editor out of
  // the way of the marker the user is placing (the old floating popover sat
  // right on top of the drop point).
  dialogTwoCol: {
    display: 'flex',
    ...shorthands.gap('16px'),
    height: 'min(64vh, 640px)',
  },
  dialogLeftRail: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
    width: '300px',
    flexShrink: 0,
    minHeight: 0,
    overflowY: 'auto',
  },
  // Picker portals its selected-pin detail editor here. Collapses out of flow
  // when empty so the rail shows only the hint until a pin is dropped.
  dialogDetailsHost: {
    ':empty': {
      display: 'none',
    },
  },
  dialogMapCol: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
    minHeight: 0,
  },
});

// Resolve the active connectors once at module load. The architect's spec
// intentionally avoids React context here — provider doesn't change at runtime,
// so a global keeps the component code lean. If per-customer choice is ever
// needed later, swap the resolver to read from a hook — interface is unchanged.
const { MapView, Marker, Autocomplete } = getMapConnector();
const geocoder = getGeocodeConnector();

interface SelectedPin {
  lat: number;
  lng: number;
  address: string;
  placeName?: string;
  displayAddress?: string;
}

interface PinDetailsCardProps {
  selected: SelectedPin;
  pinName: string;
  onNameChange: (value: string) => void;
  onAddressChange: (value: string) => void;
  onCommit: () => void;
  showSave: boolean;
  saveLabel: string;
  saveDisabled: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  justFocusedRef: React.MutableRefObject<boolean>;
}

/**
 * The selected-pin detail editor: name field + reverse-geocoded address +
 * coordinates + an optional Save button. Presentational — all state lives in
 * the parent LocationPicker. Shared by both the floating popover and the
 * sidebar panel variants so the two stay in lockstep; the wrapper chrome
 * (popover box + arrow vs. flat panel) is supplied by the caller.
 */
const PinDetailsCard: React.FC<PinDetailsCardProps> = ({
  selected,
  pinName,
  onNameChange,
  onAddressChange,
  onCommit,
  showSave,
  saveLabel,
  saveDisabled,
  inputRef,
  justFocusedRef,
}) => {
  const styles = useStyles();
  return (
    <>
      <div className={styles.pinTitleRow}>
        <Location24Regular className={styles.pinTitleIcon} />
        <Input
          size="large"
          className={styles.pinTitleInputRoot}
          input={{ ref: inputRef, className: styles.pinTitleInput }}
          value={pinName}
          onChange={(_, data) => onNameChange(data.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
              onCommit();
            }
          }}
          onFocus={(e) => {
            justFocusedRef.current = true;
            e.currentTarget.select();
          }}
          onMouseUp={(e) => {
            if (justFocusedRef.current) {
              e.preventDefault();
              justFocusedRef.current = false;
            }
          }}
          onBlur={() => {
            justFocusedRef.current = false;
          }}
          placeholder={selected.placeName ?? 'Name this pin'}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      <Textarea
        className={styles.addressInputRoot}
        textarea={{
          className: styles.addressInput,
          rows: 2,
          autoComplete: 'off',
          spellCheck: false,
          'aria-label': 'Street address',
        }}
        value={selected.displayAddress ?? selected.address}
        onChange={(_, data) => onAddressChange(data.value)}
        onMouseDown={(e) => e.stopPropagation()}
        placeholder="Street address"
      />
      <div className={styles.selectedCoords}>
        {selected.lat.toFixed(6)}, {selected.lng.toFixed(6)}
      </div>
      {showSave && (
        <Button
          appearance="primary"
          size="small"
          disabled={saveDisabled}
          onClick={onCommit}
          style={{ alignSelf: 'flex-end', marginTop: '4px' }}
        >
          {saveLabel}
        </Button>
      )}
    </>
  );
};

/**
 * Location picker — uses the active map provider connector (`google` default,
 * `mapbox` fallback) for tiles + autocomplete, and the Mapbox geocode
 * connector for reverse-geocode of pin drops/drags during the Phase 2
 * transitional state (Google geocoding lands in Phase 3 #324).
 *
 * Pin-drop trigger is left-click on the map — uniform across providers per
 * the architect's Phase 1 decision in #321. Confirm-and-name popover UI is
 * unchanged.
 *
 * Behavior preserved from the historical Mapbox-only build:
 *  - Search box with provider autocomplete (session-token grouped for billing)
 *  - Click the map to drop a pin; click a labeled POI icon to snap to that
 *    business / landmark (auto-fills its name + address, Google provider only)
 *  - Drag the pin to fine-tune (re-geocode on drop)
 *  - Reverse-geocoded address surfaced to the parent via `onLocationSelect`
 *  - Streets / Satellite toggle in the upper-right
 *  - Optional `savedLocations` overlay with hover tooltip + click-to-select
 */
const LocationPicker: React.FC<LocationPickerProps> = ({
  onLocationSelect,
  initialLocation,
  height = '400px',
  size = 'medium',
  defaultSatellite = false,
  mapInDialog = false,
  dialogOpen,
  onDialogOpenChange,
  dialogActions,
  onSave,
  saveLabel = 'Save',
  saveDisabled = false,
  resetSignal,
  fillHeight = false,
  savedLocations,
  onSavedLocationClick,
  detailsPlacement = 'popover',
  detailsPortalTarget,
}) => {
  const isDialogControlled = dialogOpen !== undefined;
  const styles = useStyles();
  const isMobile = useIsMobile();
  // Phones get a full-screen, chrome-free map: the dialog surface fills the
  // viewport, the search goes full-width, the zoom-to-fit control is dropped,
  // and the Cancel / Add buttons float over the map instead of in a footer.
  const mobileFullScreen = mapInDialog && isMobile;
  // Desktop dialog (mapInDialog, non-mobile): render a two-column layout with
  // the pin-detail editor in a left rail instead of a popover floating over the
  // marker. The picker portals its panel editor into `dialogDetailsHost`.
  const inDesktopDialog = mapInDialog && !isMobile;
  const [dialogDetailsHost, setDialogDetailsHost] = useState<HTMLDivElement | null>(null);
  // In the desktop dialog we force panel placement into the dialog's left rail;
  // otherwise honor the caller's props (popover default, or the customer tab's
  // own panel host).
  const effectiveDetailsPlacement = inDesktopDialog ? 'panel' : detailsPlacement;
  const effectiveDetailsPortalTarget = inDesktopDialog ? dialogDetailsHost : detailsPortalTarget;
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapHandleRef = useRef<MapHandle | null>(null);
  const onLocationSelectRef = useRef(onLocationSelect);
  const onSavedLocationClickRef = useRef(onSavedLocationClick);
  useEffect(() => { onSavedLocationClickRef.current = onSavedLocationClick; }, [onSavedLocationClick]);
  const [address, setAddress] = useState(initialLocation?.address || '');
  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
    address: string;
    placeName?: string;
    displayAddress?: string;
  } | null>(initialLocation || null);
  const [isSearching, setIsSearching] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  // Default to satellite on the mobile full-screen picker (aerial imagery is
  // the more useful base for dropping a precise service-location pin) — and
  // whenever a caller opts in via `defaultSatellite`.
  const [isSatellite, setIsSatellite] = useState(mobileFullScreen || defaultSatellite);
  // Hide the floating pin popover during marker drag so the user can see
  // the underlying terrain while picking the spot.
  const [isDragging, setIsDragging] = useState(false);
  // Tracks whether the editable marker is currently inside the map viewport.
  // If the user pans/zooms the pin off-screen, the popover would otherwise
  // hover in odd places (or beyond the container). Recomputed on every
  // move/zoom/resize via `updateMarkerPixel`. Defaults to true so the popover
  // appears immediately on the first placeMarker, before any move event.
  const [isMarkerInView, setIsMarkerInView] = useState(true);

  const [pinName, setPinName] = useState(initialLocation?.name ?? '');
  // Tracks whether the user has manually edited the pin name. Once true, we
  // stop auto-overwriting it from POI names so the user's label sticks even
  // if they drag/refine the marker afterwards.
  const pinNameTouchedRef = useRef(!!initialLocation?.name);
  // Tracks whether the user has hand-edited the street address. Once true, a
  // fine-tune drag keeps their text instead of clobbering it with a fresh
  // reverse-geocode. Explicit re-selections (search, suggestion, POI, a brand
  // new drop) reset it so the authoritative address flows in. Mirrors
  // pinNameTouchedRef. userAddressRef holds the last hand-typed value so the
  // geocode sites can substitute it when touched.
  const addressTouchedRef = useRef(false);
  const userAddressRef = useRef('');
  // Pixel coordinates of the marker within the map container — drives the
  // popover that floats above the pin. Recomputed on map move/zoom/resize
  // and during marker drag via the connector's projectLatLng handle.
  const [markerPixel, setMarkerPixel] = useState<{ x: number; y: number } | null>(null);
  const pinNameInputRef = useRef<HTMLInputElement>(null);
  // Tracks whether the next mouseup on the pin-name input is the one that
  // accompanies the focusing click — we suppress that mouseup's default to
  // keep the auto-selection set in onFocus from being cleared.
  const pinNameJustFocusedRef = useRef(false);
  // Set after a fresh pin drop with no auto-populated name. The effect
  // below picks it up once the popover (gated on markerPixel) has rendered
  // and the input ref is bound, then focuses the field so the user can
  // immediately type a name.
  const [pendingPinNameFocus, setPendingPinNameFocus] = useState(false);

  useEffect(() => {
    if (!pendingPinNameFocus) return;
    if (!markerPixel) return;
    const el = pinNameInputRef.current;
    if (!el) return;
    el.focus();
    setPendingPinNameFocus(false);
  }, [pendingPinNameFocus, markerPixel]);

  // Popover placement is computed each time the marker pixel, container
  // size, or popover size changes — so the box flips below the pin near the
  // top edge, clamps horizontally to stay inside the map, and avoids the
  // search overlay band at the top.
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverPlacement, setPopoverPlacement] = useState<'above' | 'below'>('above');
  const [popoverLeft, setPopoverLeft] = useState(0);
  const [popoverTop, setPopoverTop] = useState(0);
  const [arrowLeftPx, setArrowLeftPx] = useState(140);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // The autocomplete dropdown only shows while the user is actively typing. Any
  // pin drop (suggestion pick, map click, search) flips this false so the list
  // can't linger over the floating pin popover. Mirrors the website picker.
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Tracks the mouseup that accompanies a focusing click on the search box so we
  // can suppress it — otherwise it collapses the select-all set in onFocus.
  const searchJustFocusedRef = useRef(false);
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const mapDialogOpen = isDialogControlled ? !!dialogOpen : internalDialogOpen;
  const setMapDialogOpen = (open: boolean) => {
    if (!isDialogControlled) setInternalDialogOpen(open);
    onDialogOpenChange?.(open);
  };

  // The editable marker's current position. Drives the <Marker> render —
  // null means no marker on screen. Coordinates are the source of truth; the
  // marker DOM is owned by the connector.
  const [editablePos, setEditablePos] = useState<{ lat: number; lng: number } | null>(
    initialLocation ? { lat: initialLocation.lat, lng: initialLocation.lng } : null,
  );

  // Keep callback ref up to date.
  useEffect(() => {
    onLocationSelectRef.current = onLocationSelect;
  }, [onLocationSelect]);

  // Mirror pinName into a ref so handlers (registered once) read the latest
  // value instead of stale closure value.
  const pinNameRef = useRef(pinName);
  useEffect(() => {
    pinNameRef.current = pinName;
  }, [pinName]);

  // Recompute marker pixel coords via the connector's project handle. Called
  // on map move/zoom/resize and after `placeMarker`.
  const updateMarkerPixel = useCallback(() => {
    const handle = mapHandleRef.current;
    if (!handle || !editablePos) {
      setMarkerPixel(null);
      return;
    }
    const px = handle.projectLatLng(editablePos);
    setMarkerPixel(px);
    // No public bounds API on MapHandle — `projectLatLng` returning a finite
    // (x, y) inside the container is the same signal. We approximate
    // "in view" by checking the pixel falls within the container rect.
    const container = mapContainerRef.current;
    if (px && container) {
      const inView =
        px.x >= 0 && px.x <= container.clientWidth && px.y >= 0 && px.y <= container.clientHeight;
      setIsMarkerInView(inView);
    } else {
      setIsMarkerInView(true);
    }
  }, [editablePos]);

  // Re-sync the popover anchor every time the editable position changes — fresh
  // pin drops, drags, and parent-driven row selection.
  useEffect(() => {
    updateMarkerPixel();
  }, [updateMarkerPixel]);

  // Zoom-to-extents — fit the viewport to all visible markers via the
  // connector's fitBounds handle.
  const zoomToExtents = useCallback(() => {
    const handle = mapHandleRef.current;
    if (!handle) return;
    const points: Array<{ lat: number; lng: number }> = [];
    (savedLocations || []).forEach((loc) => points.push({ lat: loc.lat, lng: loc.lng }));
    if (editablePos) points.push(editablePos);
    if (points.length === 0) return;
    handle.fitBounds(points, { padding: 60, maxZoom: 19, duration: 600 });
  }, [savedLocations, editablePos]);

  // One-shot initial zoom-to-extents. When the map first has saved pins to
  // show, fit the viewport to their bounding box instead of the connector's
  // default center — so the Locations tab opens framed on the customer's
  // pins. Guarded by didFitRef so it fires exactly once: manual pans/zooms
  // and row selections afterwards keep the user's viewport (row selection
  // drives initialLocation, which flies the editable pin — we don't fight it).
  // maxZoom clamps a lone pin to a neighborhood view instead of slamming to
  // street level. 0 saved pins → skip and let the default center stand (and
  // don't burn the one-shot before pins load). Reuses the connector's
  // fitBounds; no hand-rolled bounds math.
  const didFitRef = useRef(false);
  useEffect(() => {
    if (didFitRef.current) return;
    if (!mapLoaded) return;
    const handle = mapHandleRef.current;
    if (!handle) return;
    const points = (savedLocations || [])
      .filter((loc) => Number.isFinite(loc.lat) && Number.isFinite(loc.lng))
      .map((loc) => ({ lat: loc.lat, lng: loc.lng }));
    if (points.length === 0) return;
    didFitRef.current = true;
    handle.fitBounds(points, { padding: 60, maxZoom: 14, duration: 0 });
  }, [mapLoaded, savedLocations]);

  // Reposition the popover so it stays inside the map and clear of the
  // search overlay strip. Caller invokes this whenever the marker, map
  // size, or popover size changes.
  const recomputePopover = useCallback(() => {
    const container = mapContainerRef.current;
    const popover = popoverRef.current;
    if (!container || !popover || !markerPixel) return;
    const POPOVER_WIDTH = 280;
    const PIN_GAP = 48;            // gap between pin tip and popover edge
    const SEARCH_OVERLAY_BOTTOM = 64; // top:12 + ~44px input + buffer
    const SIDE_PADDING = 8;
    const containerW = container.clientWidth;
    const containerH = container.clientHeight;
    const popoverH = popover.offsetHeight;

    const aboveTop = markerPixel.y - PIN_GAP - popoverH;
    const belowTop = markerPixel.y + PIN_GAP;

    let placement: 'above' | 'below';
    let top: number;
    if (aboveTop >= SEARCH_OVERLAY_BOTTOM) {
      placement = 'above';
      top = aboveTop;
    } else if (belowTop + popoverH <= containerH - SIDE_PADDING) {
      placement = 'below';
      top = belowTop;
    } else {
      // No clean fit — keep above and pin below the search overlay.
      placement = 'above';
      top = SEARCH_OVERLAY_BOTTOM;
    }

    let left = markerPixel.x - POPOVER_WIDTH / 2;
    left = Math.max(
      SIDE_PADDING,
      Math.min(left, containerW - POPOVER_WIDTH - SIDE_PADDING),
    );

    // Arrow points back at the pin in popover-local coords, clamped so it
    // stays within the rounded corners.
    const arrowX = markerPixel.x - left;
    const clampedArrowX = Math.max(12, Math.min(arrowX, POPOVER_WIDTH - 12));

    setPopoverPlacement(placement);
    setPopoverLeft(left);
    setPopoverTop(top);
    setArrowLeftPx(clampedArrowX);
  }, [markerPixel]);

  useEffect(() => {
    recomputePopover();
  }, [recomputePopover]);

  // Re-run on container or popover resize (dialog opens, content reflows).
  useEffect(() => {
    const container = mapContainerRef.current;
    const popover = popoverRef.current;
    if (!container || !popover || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => recomputePopover());
    observer.observe(container);
    observer.observe(popover);
    return () => observer.disconnect();
  }, [recomputePopover, selectedLocation, mapDialogOpen, mapLoaded]);

  // Drop / move the editable pin. flyTo: true (default) pans + zooms in.
  const placeMarker = useCallback(
    (lng: number, lat: number, options: { flyTo?: boolean } = {}) => {
      const handle = mapHandleRef.current;
      // Any explicit (re)placement — map click, search, suggestion, POI, or a
      // parent-supplied pin — is a deliberate new location, so a hand-edited
      // address no longer applies. Marker drag does NOT route through here, so
      // an in-place fine-tune preserves the user's edit (see handleMarkerDragEnd).
      addressTouchedRef.current = false;
      userAddressRef.current = '';
      setEditablePos({ lat, lng });
      if (handle && options.flyTo !== false) {
        // Building-detail zoom — placing a pin is almost always followed by
        // a drag to fine-tune, so land close.
        handle.flyTo({ lat, lng });
      }
      // updateMarkerPixel will fire via the editablePos effect.
    },
    [],
  );

  // Place / re-place marker whenever the parent supplies a new initialLocation
  // whose coordinates differ from the marker's current position. Skipping
  // matching coords prevents loops when the parent feeds back a location the
  // user just clicked on the map. When the parent clears initialLocation,
  // wipe internal state so the picker doesn't carry stale address/pin data
  // into the next session.
  useEffect(() => {
    if (!mapLoaded) return;
    if (!initialLocation) {
      setEditablePos(null);
      setSelectedLocation(null);
      setAddress('');
      setPinName('');
      pinNameRef.current = '';
      pinNameTouchedRef.current = false;
      addressTouchedRef.current = false;
      userAddressRef.current = '';
      setMarkerPixel(null);
      return;
    }
    const coordsMatch =
      !!editablePos &&
      Math.abs(editablePos.lng - initialLocation.lng) < 1e-6 &&
      Math.abs(editablePos.lat - initialLocation.lat) < 1e-6;

    if (!coordsMatch) {
      placeMarker(initialLocation.lng, initialLocation.lat);
    }
    setSelectedLocation(initialLocation);
    setAddress(initialLocation.address);
    const incomingName = initialLocation.name ?? '';
    setPinName(incomingName);
    pinNameRef.current = incomingName;
    pinNameTouchedRef.current = incomingName.length > 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded, initialLocation, placeMarker]);

  // Imperative reset triggered by the parent bumping `resetSignal`. Same wipe
  // as the null-initialLocation branch above, but driven by an explicit signal
  // so parents whose `initialLocation` has been null all along (the
  // user-drops-a-pin-from-scratch flow) can still clear stale internal state
  // after a save.
  const resetSignalFirstRunRef = useRef(true);
  useEffect(() => {
    if (resetSignalFirstRunRef.current) {
      resetSignalFirstRunRef.current = false;
      return;
    }
    setEditablePos(null);
    setSelectedLocation(null);
    setAddress('');
    setPinName('');
    pinNameRef.current = '';
    pinNameTouchedRef.current = false;
    addressTouchedRef.current = false;
    userAddressRef.current = '';
    setMarkerPixel(null);
  }, [resetSignal]);

  // Reverse-geocode helper — routes through the connector (currently always
  // Mapbox during Phase 2 #323; Google adapter lands in Phase 3 #324).
  const reverseGeocode = useCallback(async (lng: number, lat: number): Promise<string> => {
    const res = await geocoder.reverseGeocode(lat, lng);
    return res?.formatted ?? `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  }, []);

  // Map left-click — uniform pin-drop trigger across providers (replaces the
  // historical Mapbox-only right-click flow).
  const handleMapClick = useCallback(
    async (e: { lngLat: { lat: number; lng: number }; placeId?: string }) => {
      const lng = e.lngLat.lng;
      const lat = e.lngLat.lat;
      setShowSuggestions(false);
      // Ease-zoom to every dropped pin (fine-tuning is via drag, which doesn't
      // fly). Matches the website picker.
      placeMarker(lng, lat);

      // POI click: the user tapped a labeled business / landmark. Resolve its
      // authoritative name + address, snap the pin to the POI's own location,
      // and seed the pin name from the POI label — exactly like picking it
      // from the autocomplete list (handleSuggestionSelect).
      if (e.placeId && geocoder.getPlaceDetails) {
        const details = await geocoder.getPlaceDetails(e.placeId);
        if (details) {
          // Snap to the POI's exact coordinates without a second fly-to.
          placeMarker(details.lng, details.lat, { flyTo: false });
          const display = details.placeName
            ? `${details.placeName} — ${details.address}`
            : details.address;
          const location = {
            lat: details.lat,
            lng: details.lng,
            address: display,
            placeName: details.placeName,
            displayAddress: details.placeName ? details.address : undefined,
          };
          setSelectedLocation(location);
          setAddress(display);
          // Seed pin name from the POI label if the user hasn't typed their own.
          let nextName = pinNameRef.current;
          if (details.placeName && !pinNameTouchedRef.current) {
            nextName = details.placeName;
            setPinName(details.placeName);
            pinNameRef.current = details.placeName;
          }
          if (!nextName.trim()) setPendingPinNameFocus(true);
          onLocationSelectRef.current({
            lat: details.lat,
            lng: details.lng,
            address: details.placeName ? details.address : display,
            name: nextName.trim(),
          });
          return;
        }
      }

      const geocoded = await reverseGeocode(lng, lat);
      const location = {
        lat,
        lng,
        address: geocoded,
      };
      setSelectedLocation(location);
      setAddress(geocoded);
      // A click on plain terrain repositions the single editable pin. If it
      // already carries a label (typed, or seeded from a search/POI pick),
      // keep that label intact — the user is nudging the location, not
      // starting over. Only a label-less pin clears + focuses the name field
      // so a brand-new drop can be named immediately.
      const existingName = pinNameRef.current.trim();
      if (existingName) {
        pinNameTouchedRef.current = true;
        onLocationSelectRef.current({ lat, lng, address: geocoded, name: existingName });
      } else {
        setPinName('');
        pinNameRef.current = '';
        pinNameTouchedRef.current = false;
        setPendingPinNameFocus(true);
        onLocationSelectRef.current({ lat, lng, address: geocoded, name: '' });
      }
    },
    [placeMarker, reverseGeocode],
  );

  const handleMarkerDragStart = useCallback(() => setIsDragging(true), []);
  const handleMarkerDrag = useCallback((pos: { lat: number; lng: number }) => {
    // Live update the editable pos so the popover follows the pin during drag.
    setEditablePos(pos);
  }, []);
  const handleMarkerDragEnd = useCallback(
    async (pos: { lat: number; lng: number }) => {
      setIsDragging(false);
      setEditablePos(pos);
      const geocoded = await reverseGeocode(pos.lng, pos.lat);
      const name = pinNameRef.current.trim();
      // A fine-tune drag keeps a hand-edited address (the user asserted it);
      // an untouched pin takes the fresh reverse-geocode for the new spot.
      const touched = addressTouchedRef.current;
      const addr = touched ? userAddressRef.current : geocoded;
      const location = touched
        ? { lat: pos.lat, lng: pos.lng, address: addr, displayAddress: addr }
        : { lat: pos.lat, lng: pos.lng, address: addr };
      setSelectedLocation(location);
      setAddress(addr);
      onLocationSelectRef.current({ ...location, name });
    },
    [reverseGeocode],
  );

  const searchAddress = async () => {
    if (!address.trim()) return;
    setShowSuggestions(false);
    if (selectedLocation && address.trim() === selectedLocation.address.trim()) return;

    // Coordinate paste: drop the pin directly, then reverse-geocode for
    // a friendly address string.
    const coords = parseLatLng(address);
    if (coords) {
      placeMarker(coords.lng, coords.lat);
      const geocoded = await reverseGeocode(coords.lng, coords.lat);
      const location = { lat: coords.lat, lng: coords.lng, address: geocoded };
      setSelectedLocation(location);
      setAddress(geocoded);
      const name = pinNameRef.current.trim();
      if (!name) setPendingPinNameFocus(true);
      onLocationSelect({ ...location, name });
      return;
    }

    setIsSearching(true);
    try {
      const result = await geocoder.forwardGeocode(address, { country: 'PR', types: 'address,poi' });
      if (result) {
        const { lat, lng, address: placeName } = result;
        placeMarker(lng, lat);
        const location = { lat, lng, address: placeName };
        setSelectedLocation(location);
        setAddress(placeName);
        const name = pinNameRef.current.trim();
        if (!name) setPendingPinNameFocus(true);
        onLocationSelect({ ...location, name });
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSuggestionSelect = useCallback(
    async (
      sel: { lat: number; lng: number; address: string; placeName?: string },
    ) => {
      const { lat, lng, address: textboxAddress, placeName } = sel;
      const display = placeName && textboxAddress ? `${placeName} — ${textboxAddress}` : textboxAddress;
      setShowSuggestions(false);
      placeMarker(lng, lat);
      const location = {
        lat,
        lng,
        address: display,
        placeName,
        displayAddress: placeName ? textboxAddress : undefined,
      };
      setSelectedLocation(location);
      setAddress(display);
      // Seed pin name from POI label if the user hasn't typed their own.
      let nextName = pinNameRef.current;
      if (placeName && !pinNameTouchedRef.current) {
        nextName = placeName;
        setPinName(placeName);
        pinNameRef.current = placeName;
      }
      if (!nextName.trim()) {
        setPendingPinNameFocus(true);
      }
      onLocationSelectRef.current({
        lat,
        lng,
        address: placeName ? textboxAddress : display,
        name: nextName.trim(),
      });
    },
    [placeMarker],
  );

  const handlePinNameChange = (value: string) => {
    setPinName(value);
    pinNameTouchedRef.current = value.trim().length > 0;
    // Mirror to ref immediately so the next emission (e.g. dragend) reads
    // the up-to-date value rather than waiting on the effect.
    pinNameRef.current = value;
    if (selectedLocation) {
      onLocationSelectRef.current({
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        address: selectedLocation.address,
        // Emit raw value; trimming on every keystroke round-trips through
        // the parent and clobbers trailing spaces (e.g. typing "Front ").
        // commitSave() trims at save time.
        name: value,
      });
    }
  };

  // Street-address edit from the pin card. Overrides the reverse-geocoded text
  // with the user's own. We flatten displayAddress + address to the same value
  // so both the save path (commitSave uses displayAddress ?? address) and the
  // live emission carry it; placeName is retained only for the name-field
  // placeholder.
  const handleAddressChange = (value: string) => {
    addressTouchedRef.current = true;
    userAddressRef.current = value;
    setSelectedLocation((prev) =>
      prev ? { ...prev, address: value, displayAddress: value } : prev,
    );
    if (selectedLocation) {
      onLocationSelectRef.current({
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        address: value,
        name: pinNameRef.current,
      });
    }
  };

  return (
    <div className={`${styles.root}${fillHeight ? ` ${styles.rootFill}` : ''}`}>
      <Autocomplete
        value={address}
        onChange={setAddress}
        onSelect={handleSuggestionSelect}
        country="PR"
        proximity={{ lat: 18.2208, lng: -66.5901 }}
      >
        {(api) => {
          // Reset highlight when the suggestion list changes.
          // Dropdown only shows while typing AND there are results — so it
          // disappears on pin drop and never overlaps the pin popover.
          const showList = showSuggestions && api.suggestions.length > 0;
          const handleKeyPress = (e: React.KeyboardEvent) => {
            if (e.key === 'ArrowDown') {
              if (!showList) return;
              e.preventDefault();
              setHighlightedIndex((i) => Math.min(i + 1, api.suggestions.length - 1));
            } else if (e.key === 'ArrowUp') {
              if (!showList) return;
              e.preventDefault();
              setHighlightedIndex((i) => Math.max(i - 1, -1));
            } else if (e.key === 'Escape') {
              setHighlightedIndex(-1);
              setShowSuggestions(false);
            } else if (e.key === 'Enter') {
              e.preventDefault();
              if (showList) {
                const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
                api.selectSuggestion(api.suggestions[idx]);
              } else {
                searchAddress();
              }
            }
          };
          const SearchBlock = (
            <div className={styles.searchRow}>
              <Input
                className={styles.searchInput}
                size={size}
                value={api.query}
                onChange={(_e, data) => { api.setQuery(data.value); setShowSuggestions(true); }}
                onKeyDown={handleKeyPress}
                input={{
                  onFocus: (e) => { searchJustFocusedRef.current = true; e.currentTarget.select(); },
                  onMouseUp: (e) => { if (searchJustFocusedRef.current) { e.preventDefault(); searchJustFocusedRef.current = false; } },
                  onBlur: () => { setShowSuggestions(false); searchJustFocusedRef.current = false; },
                }}
                placeholder="Enter a name or address or paste coordinates…"
                autoComplete="off"
              />
              {/* The Search button is redundant on mobile — autocomplete +
                  the keyboard's Enter key drive selection — and it crowded the
                  full-width field, so it's desktop-only. */}
              {!mobileFullScreen && (
                <Button
                  className={styles.searchButton}
                  appearance="primary"
                  icon={<Search24Regular />}
                  onClick={searchAddress}
                  disabled={isSearching}
                >
                  {isSearching ? 'Searching…' : 'Search'}
                </Button>
              )}

              {showList && (
                <ul className={styles.suggestList}>
                  {api.suggestions.map((s: AutocompleteSuggestion, i: number) => {
                    const isHighlighted = i === highlightedIndex;
                    return (
                      <li key={s.id}>
                        <button
                          type="button"
                          disabled={api.isLoading}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseEnter={() => setHighlightedIndex(i)}
                          onClick={() => api.selectSuggestion(s)}
                          className={`${styles.suggestItem}${
                            isHighlighted ? ' ' + styles.suggestItemHighlighted : ''
                          }`}
                        >
                          <Location24Regular
                            className={`${styles.suggestIcon}${
                              isHighlighted ? ' ' + styles.suggestIconHighlighted : ''
                            }`}
                          />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div className={styles.suggestName}>{s.name}</div>
                            {s.description && (
                              <div className={styles.suggestPlace}>{s.description}</div>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );

          // Shared fallback footer — used by both the desktop dialog footer and
          // the mobile overlaid action bar when the parent passes no actions.
          const defaultDialogActions = (
            <>
              <Button
                appearance="secondary"
                onClick={() => setMapDialogOpen(false)}
                style={{ whiteSpace: 'nowrap' }}
              >
                Cancel
              </Button>
              {selectedLocation ? (
                <Button
                  appearance="primary"
                  onClick={() => setMapDialogOpen(false)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Add location
                </Button>
              ) : (
                <Tooltip
                  content="Click on the map (or search) to drop a pin first."
                  relationship="description"
                >
                  <Button
                    appearance="primary"
                    disabledFocusable
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Add location
                  </Button>
                </Tooltip>
              )}
            </>
          );

          const mapHeight = (mobileFullScreen || inDesktopDialog) ? '100%' : height;
          const MapBlock = (
            <div className={`${styles.mapWrap}${(fillHeight || mobileFullScreen || inDesktopDialog) ? ` ${styles.mapWrapFill}` : ''}`}>
              <div ref={mapContainerRef} className={styles.mapContainer} style={{ height: mapHeight, position: 'relative' }}>
                <MapView
                  center={{ lat: 18.2208, lng: -66.5901 }}
                  zoom={9}
                  style={isSatellite ? 'satellite' : 'streets'}
                  onMapClick={handleMapClick}
                  onMoveOrZoom={updateMarkerPixel}
                  onLoad={() => setMapLoaded(true)}
                  mapRef={(h) => { mapHandleRef.current = h; }}
                  style_={{ width: '100%', height: '100%' }}
                >
                  {/* Editable pin */}
                  {editablePos && (
                    <Marker
                      position={editablePos}
                      color="#f97316"
                      draggable
                      onDragStart={handleMarkerDragStart}
                      onDrag={handleMarkerDrag}
                      onDragEnd={handleMarkerDragEnd}
                    />
                  )}
                  {/* Read-only saved-location pins */}
                  {(savedLocations || []).map((loc) => (
                    <Marker
                      key={loc.id}
                      position={{ lat: loc.lat, lng: loc.lng }}
                      color="#f97316"
                      tooltip={loc.name}
                      onClick={() => onSavedLocationClickRef.current?.(loc.id)}
                    />
                  ))}
                </MapView>
              </div>
              {!mapLoaded && (
                <div className={styles.loadingOverlay} style={{ height: mapHeight }}>
                  <Spinner size="medium" label="Loading map…" />
                </div>
              )}

              <div className={mobileFullScreen ? styles.searchOverlayMobile : styles.searchOverlay}>{SearchBlock}</div>

              {mapLoaded && (
                <div className={mobileFullScreen ? styles.mapControlsMobile : styles.mapControls}>
                  {/* Zoom-to-fit is dropped on the mobile full-screen picker to
                      keep the chrome minimal (per spec). */}
                  {!mobileFullScreen && (
                    <Tooltip content="Zoom to fit all locations" relationship="label">
                      <Button
                        className={styles.zoomExtentsButton}
                        appearance="subtle"
                        icon={<ArrowExpand20Regular />}
                        aria-label="Zoom to fit all locations"
                        onClick={zoomToExtents}
                      />
                    </Tooltip>
                  )}
                  <Button
                    className={styles.styleToggle}
                    size="small"
                    appearance="secondary"
                    onClick={() => setIsSatellite(!isSatellite)}
                  >
                    {isSatellite ? 'Streets' : 'Satellite'}
                  </Button>
                </div>
              )}

              {selectedLocation && (() => {
                const commitSave = () => {
                  if (!onSave || saveDisabled) return;
                  onSave({
                    lat: selectedLocation.lat,
                    lng: selectedLocation.lng,
                    address: selectedLocation.displayAddress ?? selectedLocation.address,
                    name: pinName.trim(),
                  });
                };
                const hasChanges =
                  !initialLocation ||
                  selectedLocation.lat !== initialLocation.lat ||
                  selectedLocation.lng !== initialLocation.lng ||
                  pinName.trim() !== (initialLocation.name ?? '').trim();
                const card = (
                  <PinDetailsCard
                    selected={selectedLocation}
                    pinName={pinName}
                    onNameChange={handlePinNameChange}
                    onAddressChange={handleAddressChange}
                    onCommit={commitSave}
                    showSave={!!onSave && hasChanges}
                    saveLabel={saveLabel}
                    saveDisabled={saveDisabled}
                    inputRef={pinNameInputRef}
                    justFocusedRef={pinNameJustFocusedRef}
                  />
                );

                // Sidebar variant: render the editor flat into the parent's
                // host container (a portal). Shown whenever a pin is selected —
                // it isn't anchored to the marker, so panning/dragging the pin
                // off-screen doesn't hide it.
                if (effectiveDetailsPlacement === 'panel') {
                  if (!effectiveDetailsPortalTarget) return null;
                  return createPortal(
                    <div className={styles.pinPanel}>{card}</div>,
                    effectiveDetailsPortalTarget,
                  );
                }

                // Popover variant (default): float over the map, anchored to
                // the marker pixel — needs markerPixel to position itself.
                if (!markerPixel) return null;
                return (
                <div
                  ref={popoverRef}
                  className={styles.pinPopover}
                  style={{
                    left: `${popoverLeft}px`,
                    top: `${popoverTop}px`,
                    visibility: isDragging || !isMarkerInView || showList ? 'hidden' : 'visible',
                    pointerEvents: isDragging || !isMarkerInView || showList ? 'none' : 'auto',
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onWheel={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  {card}
                  <div
                    className={`${styles.pinPopoverArrow} ${
                      popoverPlacement === 'above' ? styles.pinPopoverArrowAbove : styles.pinPopoverArrowBelow
                    }`}
                    style={{ left: `${arrowLeftPx}px` }}
                  />
                </div>
                );
              })()}

              {/* Mobile: Cancel / Add float over the bottom of the map instead
                  of living in a dialog footer. */}
              {mobileFullScreen && (
                <div className={styles.mobileActionBar}>
                  {dialogActions ?? defaultDialogActions}
                </div>
              )}
            </div>
          );

          if (mobileFullScreen) {
            return (
              <>
                {!isDialogControlled && (
                  <Button
                    appearance="secondary"
                    size={size}
                    icon={<Map24Regular />}
                    onClick={() => setMapDialogOpen(true)}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {selectedLocation ? 'Adjust on map' : 'Pick on map'}
                  </Button>
                )}
                <Dialog
                  open={mapDialogOpen}
                  onOpenChange={(_, data) => setMapDialogOpen(data.open)}
                >
                  <DialogSurface className={styles.mobileSurface}>
                    {/* Absorbs the dialog's auto-focus so the keyboard doesn't
                        open on its own — see focusSink note above. */}
                    <div tabIndex={-1} className={styles.focusSink} />
                    {MapBlock}
                  </DialogSurface>
                </Dialog>
              </>
            );
          }

          if (mapInDialog) {
            return (
              <>
                {!isDialogControlled && (
                  <Button
                    appearance="secondary"
                    size={size}
                    icon={<Map24Regular />}
                    onClick={() => setMapDialogOpen(true)}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {selectedLocation ? 'Adjust on map' : 'Pick on map'}
                  </Button>
                )}
                <Dialog
                  open={mapDialogOpen}
                  onOpenChange={(_, data) => setMapDialogOpen(data.open)}
                >
                  <DialogSurface style={{ maxWidth: '1100px', width: '94vw' }}>
                    <DialogBody>
                      <DialogTitle>Pick location on map</DialogTitle>
                      <DialogContent>
                        <div className={styles.dialogTwoCol}>
                          <div className={styles.dialogLeftRail}>
                            <div className={styles.dialogHint}>
                              You can search for a location or click anywhere on the map to drop a pin.
                            </div>
                            {/* Selected-pin editor portals in here, beside the map. */}
                            <div ref={setDialogDetailsHost} className={styles.dialogDetailsHost} />
                          </div>
                          <div className={styles.dialogMapCol}>{MapBlock}</div>
                        </div>
                      </DialogContent>
                      <DialogActions
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          flexWrap: 'nowrap',
                          justifyContent: 'flex-end',
                          gap: '8px',
                        }}
                      >
                        {dialogActions ?? defaultDialogActions}
                      </DialogActions>
                    </DialogBody>
                  </DialogSurface>
                </Dialog>
              </>
            );
          }
          return MapBlock;
        }}
      </Autocomplete>
    </div>
  );
};

export default LocationPicker;
